import React, { useEffect, useState } from 'react';
import axios from 'axios';
import EmailList from '../components/EmailList';
import CouponsList from '../components/CouponsList';
import AccountCodesList from '../components/AccountCodesList';
import AIAssistant from '../components/AIAssistant';
import Navigation from '../components/Navigation';
import './Dashboard.css';

function Dashboard({ user }) {
  const [activeTab, setActiveTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [accountCodes, setAccountCodes] = useState([]);
  const [spamEmails, setSpamEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [sortBy, setSortBy] = useState('importance');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('uiTheme') || 'dark');
  const [compactMode, setCompactMode] = useState(() => localStorage.getItem('compactMode') === 'true');
  const [showAIFab, setShowAIFab] = useState(() => localStorage.getItem('showAIFab') !== 'false');

  const fetchEmailsPage = async (pageNumber, append = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/emails?sort=${sortBy}&page=${pageNumber}&limit=200`);
      const allEmails = response.data.emails;
      const pagination = response.data.pagination;

      const spam = allEmails.filter(email => email.isSpam);
      const regularEmails = allEmails.filter(email =>
        !email.isSpam && !email.isCoupon && !email.isAccountCode
      );

      setEmails(prev => (append ? [...prev, ...regularEmails] : regularEmails));
      setSpamEmails(prev => (append ? [...prev, ...spam] : spam));
      setLastRefresh(new Date());
      setHasMore(pagination.page < pagination.pages);
      setPage(pagination.page);
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmails = async () => {
    await fetchEmailsPage(1, false);
  };

  const loadMoreEmails = async () => {
    if (!hasMore || loading) return;
    await fetchEmailsPage(page + 1, true);
  };

  const loadAllEmails = async () => {
    setLoadingAll(true);
    try {
      let currentPage = 1;
      let hasNext = true;
      let collectedEmails = [];
      let collectedSpam = [];

      while (hasNext) {
        const response = await axios.get(`/api/emails?sort=${sortBy}&page=${currentPage}&limit=200`);
        const allEmails = response.data.emails;
        const pagination = response.data.pagination;

        collectedSpam = [...collectedSpam, ...allEmails.filter(email => email.isSpam)];
        collectedEmails = [
          ...collectedEmails,
          ...allEmails.filter(email => !email.isSpam && !email.isCoupon && !email.isAccountCode)
        ];

        hasNext = pagination.page < pagination.pages;
        currentPage += 1;
      }

      setEmails(collectedEmails);
      setSpamEmails(collectedSpam);
      setHasMore(false);
      setPage(currentPage - 1);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading all emails:', error);
    } finally {
      setLoadingAll(false);
    }
  };

  const scanEmails = async (full = false) => {
    setScanning(true);
    try {
      await axios.post(`/api/emails/scan${full ? '?full=true' : ''}`);
      await fetchEmails();
      alert(full ? 'Full email scan completed!' : 'Email scan completed! Your emails are now up to date.');
    } catch (error) {
      console.error('Error scanning emails:', error);
      alert('Error scanning emails. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const sendEmail = async () => {
    if (!composeTo || !composeSubject || !composeBody) {
      alert('Please fill out To, Subject, and Message.');
      return;
    }

    setSending(true);
    try {
      await axios.post('/api/emails/send', {
        to: composeTo,
        subject: composeSubject,
        message: composeBody
      });
      alert('Email sent successfully!');
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      setShowCompose(false);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/coupons');
      setCoupons(response.data.coupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/account-codes');
      setAccountCodes(response.data.accountCodes);
    } catch (error) {
      console.error('Error fetching account codes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.dataset.theme = theme;
    localStorage.setItem('uiTheme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('compactMode', compactMode.toString());
  }, [compactMode]);

  useEffect(() => {
    localStorage.setItem('showAIFab', showAIFab.toString());
  }, [showAIFab]);

  useEffect(() => {
    const loadData = () => {
      if (activeTab === 'inbox') {
        fetchEmails();
      } else if (activeTab === 'coupons') {
        fetchCoupons();
      } else if (activeTab === 'accounts') {
        fetchAccountCodes();
      } else if (activeTab === 'spam') {
        fetchEmails(); // Spam is fetched with emails
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sortBy]);

  const handleLogout = async () => {
    try {
      await axios.get('/auth/logout');
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className={`dashboard ${compactMode ? 'compact' : ''}`}>
      <div className="nav-hover-zone">
        <div className="nav-hover-target"></div>
        <Navigation user={user} onLogout={handleLogout} />
      </div>
      
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>📧 Your Email Dashboard</h1>
          <div className="header-info">
            <span className="last-refresh">
              Last scan: {lastRefresh.toLocaleTimeString()}
            </span>
            <button 
              className="scan-btn" 
              onClick={() => scanEmails(false)}
              disabled={scanning}
            >
              {scanning ? '⏳ Scanning...' : '🔍 Scan Gmail'}
            </button>
            <button 
              className="scan-btn" 
              onClick={() => scanEmails(true)}
              disabled={scanning}
            >
              {scanning ? '⏳ Scanning...' : '🌐 Full Scan'}
            </button>
            <button className="refresh-btn" onClick={fetchEmails}>
              🔄 Refresh
            </button>
            <button className="refresh-btn" onClick={() => setShowCompose(true)}>
              ✉️ Compose
            </button>
          </div>
        </div>

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'inbox' ? 'active' : ''}`}
            onClick={() => setActiveTab('inbox')}
          >
            <span className="tab-icon">📥</span>
            <span className="tab-label">Inbox</span>
            <span className="tab-count">{emails.length}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => setActiveTab('coupons')}
          >
            <span className="tab-icon">🎟️</span>
            <span className="tab-label">Deals</span>
            <span className="tab-count">{coupons.length}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounts')}
          >
            <span className="tab-icon">🔐</span>
            <span className="tab-label">Codes</span>
            <span className="tab-count">{accountCodes.length}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'spam' ? 'active' : ''}`}
            onClick={() => setActiveTab('spam')}
          >
            <span className="tab-icon">🗑️</span>
            <span className="tab-label">Spam</span>
            <span className="tab-count">{spamEmails.length}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <span className="tab-icon">⚙️</span>
            <span className="tab-label">Settings</span>
          </button>
        </div>

        {activeTab === 'inbox' && (
          <div className="tab-content">
            <div className="sort-options">
              <label>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="importance">⭐ Importance</option>
                <option value="recent">📅 Most Recent</option>
                <option value="oldest">🕒 Oldest First</option>
              </select>
              <button className="refresh-btn" onClick={loadMoreEmails} disabled={!hasMore || loading}>
                {loading ? '⏳ Loading...' : hasMore ? '➕ Load More' : '✅ All Loaded'}
              </button>
              <button className="scan-btn" onClick={loadAllEmails} disabled={loadingAll}>
                {loadingAll ? '⏳ Loading All...' : '📚 Load All'}
              </button>
            </div>
            <EmailList emails={emails} loading={loading} onRefresh={fetchEmails} />
          </div>
        )}

        {activeTab === 'coupons' && (
          <div className="tab-content">
            <CouponsList coupons={coupons} loading={loading} />
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="tab-content">
            <AccountCodesList accountCodes={accountCodes} loading={loading} />
          </div>
        )}

        {activeTab === 'spam' && (
          <div className="tab-content">
            <div className="spam-warning">
              <p>⚠️ These emails were automatically identified as spam or promotional content.</p>
            </div>
            <EmailList emails={spamEmails} loading={loading} onRefresh={fetchEmails} />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="tab-content settings-panel">
            <h3>Appearance</h3>
            <div className="settings-row">
              <div className="settings-label">
                <strong>Theme</strong>
                <span>Switch between light and dark mode.</span>
              </div>
              <select value={theme} onChange={(event) => setTheme(event.target.value)}>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </div>

            <h3>Layout</h3>
            <div className="settings-row">
              <div className="settings-label">
                <strong>Compact view</strong>
                <span>Reduce spacing for denser lists.</span>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={compactMode}
                  onChange={(event) => setCompactMode(event.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>

            <h3>AI Assistant</h3>
            <div className="settings-row">
              <div className="settings-label">
                <strong>Show AI button</strong>
                <span>Toggle the floating AI assistant button.</span>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={showAIFab}
                  onChange={(event) => setShowAIFab(event.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        )}

        {showAI && <AIAssistant onClose={() => setShowAI(false)} />}

        {showCompose && (
          <div className="compose-modal">
            <div className="compose-container">
              <div className="compose-header">
                <h2>✉️ New Email</h2>
                <button className="close-btn" onClick={() => setShowCompose(false)}>×</button>
              </div>
              <div className="compose-body">
                <label>
                  To
                  <input
                    type="email"
                    value={composeTo}
                    onChange={(event) => setComposeTo(event.target.value)}
                    placeholder="recipient@example.com"
                  />
                </label>
                <label>
                  Subject
                  <input
                    type="text"
                    value={composeSubject}
                    onChange={(event) => setComposeSubject(event.target.value)}
                    placeholder="Subject"
                  />
                </label>
                <label>
                  Message
                  <textarea
                    rows="6"
                    value={composeBody}
                    onChange={(event) => setComposeBody(event.target.value)}
                    placeholder="Write your email..."
                  />
                </label>
              </div>
              <div className="compose-actions">
                <button className="refresh-btn" onClick={() => setShowCompose(false)}>
                  Cancel
                </button>
                <button className="scan-btn" onClick={sendEmail} disabled={sending}>
                  {sending ? '⏳ Sending...' : '📤 Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAIFab && (
          <button className="ai-assistant-btn" onClick={() => setShowAI(true)}>
            🤖
          </button>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
